/**
 * CartAdd — central add-to-cart utilities shared by product-upsell,
 * backside-engraving-modal, optional-file-input, and product-form.
 *
 * Exposed as window.CartAdd so every deferred script can reach it.
 */
window.CartAdd = (() => {
  /* ========== DOM helpers ========== */

  function getCart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer')
  }

  function getSectionsConfig(cart) {
    return {
      sections: cart ? cart.getSectionsToRender().map(s => s.id) : [],
      sectionsUrl: window.location.pathname,
    }
  }

  function setButtonLoading(button, productFormEl, isLoading) {
    if (!button) return
    const spinner = productFormEl?.querySelector('.loading__spinner')
    if (isLoading) {
      button.setAttribute('aria-disabled', 'true')
      button.classList.add('loading')
      if (spinner) spinner.classList.remove('hidden')
    } else {
      button.classList.remove('loading')
      button.removeAttribute('aria-disabled')
      if (spinner) spinner.classList.add('hidden')
    }
  }

  /* ========== Validation ========== */

  function validateInputModal(section, form) {
    const formId = form?.id
    const inputModal =
      (formId && document.querySelector(`input-modal[data-product-form-id="${formId}"]`)) ||
      section?.querySelector('input-modal')
    if (!inputModal) return { valid: true }
    if (typeof inputModal.isYesSelected !== 'function' || !inputModal.isYesSelected())
      return { valid: true }
    const result = inputModal.validateRequiredFields()
    if (result.valid) return { valid: true }
    return { valid: false, invalidFields: result.invalidFields, inputModal }
  }

  /* ========== Property helpers ========== */

  function isEngravingNo(section) {
    return !!section?.querySelector(
      'backside-engraving-modal [data-engraving-toggle][value="no"]'
    )?.checked
  }

  function getUpsellGroupPropertyNamesToSkip(section) {
    const names = new Set()
    if (!section) return names
    section.querySelectorAll('[data-upsell-group]:not([hidden])').forEach(group => {
      const selected = group.querySelector('[data-upsell-item].is-selected')
      const variantIdInput = group.querySelector('[data-upsell-variant-id]')
      const propertyInput = group.querySelector('[data-upsell-property]')
      const hasOwnLineItem =
        variantIdInput && variantIdInput.value && variantIdInput.value.trim() !== ''
      if (selected && hasOwnLineItem && propertyInput?.name) {
        names.add(propertyInput.name)
      }
    })
    return names
  }

  /**
   * Build a plain-object properties map for the main product line item.
   *
   * @param {FormData} formData
   * @param {Object}   options
   * @param {Element}  [options.section]              - section element for querying engraving/upsell state
   * @param {boolean}  [options.skipFiles=true]        - skip File values (can't be JSON-serialised)
   * @param {boolean}  [options.skipVariantProp=true]  - skip the "Variant" property (belongs on upsell line item)
   * @param {boolean}  [options.skipEngravingWhenNo=false] - skip Engraving props when engraving toggle is "no"
   * @param {boolean}  [options.skipAllEngraving=false]    - skip ALL Engraving props unconditionally
   * @param {boolean}  [options.skipUpsellGroupProps=false] - skip props whose upsell group has its own line item
   * @param {Function} [options.keyFilter]             - optional (key, value) => false to skip
   */
  function buildMainProperties(formData, options = {}) {
    const {
      section = null,
      skipFiles = true,
      skipVariantProp = true,
      skipEngravingWhenNo = false,
      skipAllEngraving = false,
      skipUpsellGroupProps = false,
      keyFilter = null,
    } = options

    const properties = {}
    const engravingNo = skipEngravingWhenNo ? isEngravingNo(section) : false
    const upsellSkip = skipUpsellGroupProps
      ? getUpsellGroupPropertyNamesToSkip(section)
      : new Set()

    for (const [key, value] of formData.entries()) {
      if (!key.startsWith('properties[') || !value) continue
      if (skipFiles && value instanceof File) continue
      if (upsellSkip.has(key)) continue
      if (keyFilter && !keyFilter(key, value)) continue
      const propName = key.slice('properties['.length, -1)
      if (skipVariantProp && propName === 'Variant') continue
      if (skipAllEngraving && propName.includes('Engraving -')) continue
      if (engravingNo && propName.includes('Engraving -')) continue
      properties[propName] = value
    }
    return properties
  }

  /* ========== Upsell / optional-file item collectors ========== */

  function getUpsellItems(section) {
    if (!section) return []
    const items = []
    const seenIds = new Set()
    section.querySelectorAll('product-upsell').forEach(el => {
      let elItems = []
      if (typeof el.getAllSectionUpsellItems === 'function') {
        elItems = el.getAllSectionUpsellItems()
      } else if (typeof el.getActiveUpsellItems === 'function') {
        elItems = el.getActiveUpsellItems()
      } else if (typeof el.getActiveUpsellVariantId === 'function') {
        const id = parseInt(el.getActiveUpsellVariantId(), 10)
        if (id && !Number.isNaN(id)) elItems = [{ id, quantity: 1 }]
      }
      elItems.forEach(item => {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id)
          items.push(item)
        }
      })
    })
    getCharmItems(section).forEach(item => {
      if (seenIds.has(item.id)) {
        const existing = items.find(i => i.id === item.id)
        if (existing) existing.quantity += item.quantity
      } else {
        seenIds.add(item.id)
        items.push(item)
      }
    })
    return items
  }

  /** Charm selections from every input-modal in the section (their pickers live in body-appended overlays). */
  function getCharmItems(section) {
    if (!section) return []
    const items = []
    section.querySelectorAll('input-modal').forEach(el => {
      if (typeof el.getCharmItems === 'function') {
        el.getCharmItems().forEach(item => items.push(item))
      }
    })
    return items
  }

  function getOptionalFileItem(section) {
    const el = section?.querySelector('optional-file-input')
    if (!el) return null
    if (typeof el.isYesSelected !== 'function' || !el.isYesSelected()) return null
    if (typeof el.getLinkedProductId !== 'function') return null
    if (typeof el.shouldShowFakeButton === 'function' && el.shouldShowFakeButton()) return null
    const id = el.getLinkedProductId()
    if (!id) return null
    return { id: parseInt(id, 10), quantity: 1 }
  }

  function optionalFileHasFile(section) {
    const el = section?.querySelector('optional-file-input')
    return !!(el && typeof el.hasFile === 'function' && el.hasFile())
  }

  /* ========== Network ========== */

  function postJSON(items, sections, sectionsUrl) {
    return fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items, sections, sections_url: sectionsUrl }),
    }).then(r => r.json())
  }

  function postFormData(fd) {
    return fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: fd,
    }).then(r => r.json())
  }

  /* ========== Response handling ========== */

  /**
   * Shared response handler: publishes cart events, shows errors, renders cart.
   *
   * @returns {boolean} true when the add was successful.
   */
  function handleResponse(response, { cart, productFormEl, mainVariantId, onSuccess, onError }) {
    if (response.status) {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'product-form',
        productVariantId: mainVariantId,
        errors: response.errors || response.description,
        message: response.message,
      })
      if (productFormEl?.handleErrorMessage) productFormEl.handleErrorMessage(response.description)
      if (onError) onError(response)
      return false
    }
    if (!cart) {
      window.location = routes.cart_url
      return true
    }
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'product-form',
      productVariantId: mainVariantId,
      cartData: response,
    })
    cart.renderContents(response)
    if (onSuccess) onSuccess(response)
    return true
  }

  /* ========== Misc ========== */

  function generateBasketGroupId() {
    return `basket_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
  }

  /**
   * Build raw FormData for the main product, filtering engraving and upsell
   * group properties the same way buildMainProperties does but keeping File
   * values intact (required for Shopify file uploads).
   */
  function buildMainFormData(
    form,
    {
      mainVariantId,
      quantity,
      sections,
      sectionsUrl,
      section,
      basketGroupId = null,
      skipAllEngraving = false,
      skipEngravingWhenNo = false,
      engravingKeyFilter = null,
      engravingDisplayNameFn = null,
    }
  ) {
    const formData = new FormData(form)
    const fd = new FormData()
    fd.append('id', mainVariantId)
    fd.append('quantity', quantity)
    if (basketGroupId) fd.append('properties[_basket_group]', basketGroupId)

    const upsellSkip = getUpsellGroupPropertyNamesToSkip(section)
    const engravingNo = skipEngravingWhenNo ? isEngravingNo(section) : false

    for (const [key, value] of formData.entries()) {
      if (!key.startsWith('properties[')) continue
      if (upsellSkip.has(key)) continue
      const propName = key.slice('properties['.length, -1)
      if (skipAllEngraving && propName.includes('Engraving -')) continue
      if (engravingNo && propName.includes('Engraving -')) continue
      if (engravingKeyFilter && propName.includes('Engraving -') && !engravingKeyFilter(key))
        continue

      const displayName =
        engravingDisplayNameFn && engravingKeyFilter?.(key)
          ? engravingDisplayNameFn(propName)
          : propName
      if (value instanceof File) {
        fd.append(`properties[${displayName}]`, value)
      } else if (value != null && value !== '') {
        fd.append(`properties[${displayName}]`, String(value))
      }
    }

    fd.append('sections', sections.join(','))
    fd.append('sections_url', sectionsUrl)
    return fd
  }

  /* ========== Public API ========== */

  return {
    getCart,
    getSectionsConfig,
    setButtonLoading,
    validateInputModal,
    isEngravingNo,
    getUpsellGroupPropertyNamesToSkip,
    buildMainProperties,
    getUpsellItems,
    getCharmItems,
    getOptionalFileItem,
    optionalFileHasFile,
    postJSON,
    postFormData,
    handleResponse,
    generateBasketGroupId,
    buildMainFormData,
  }
})()
